import { ResolvedStatementVerdict } from './statementVerdict';
import { periodKey, rowKey } from './periods';
import { PeriodOwner } from './resolveStatementAssignments';

import { StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';

/**
 * For each addressable row, the months a *different* row of the same document is on — the months
 * that row's menu must not offer.
 *
 * Grouped by row rather than by document, because a document is not the unit a correction moves.
 * While an edit is staged, one row shows up under two months — the coverage row it still holds and
 * the month it now occupies — and both of those belong to that row. Grouped by document the two
 * were indistinguishable from a genuine second row, and a file the member had simply moved started
 * withholding the month it came from and relabelling its own controls.
 *
 * Keyed by `rowKey`, so a document coverage reports no row for collapses to one entry however many
 * months we read on it: that is exactly how many rows a correction on it can move.
 *
 * This is the menu's half of `foldStagedAssignments`' sibling refusal, and the two have to answer
 * alike — a month the fold refuses but the menu offers is a click that does nothing, with no error
 * and no snackbar to say so. Both sources feed it for that reason:
 *
 * - the months the document owns on screen, which is what the fold sees in its resolved map;
 * - its coverage rows, which the fold would *allow* a move onto once a staged edit has pointed the
 *   sibling elsewhere — and which the save would then delete, since the row is still there
 *   server-side until the write lands.
 */
export default function siblingMonthsByRow(
    verdictByUuid: Record<string, ResolvedStatementVerdict>,
    ownerByPeriodKey: Map<string, PeriodOwner>
): Map<string, Set<string>> {
    /** Document → row → the months that row holds or occupies, as `periodKey`s. */
    const rowsByDocument = new Map<string, Map<string, Set<string>>>();
    const add = (documentUuid: string, sourcePeriod: StatementPeriod | undefined, monthKey: string) => {
        const rows = rowsByDocument.get(documentUuid) ?? new Map<string, Set<string>>();
        rowsByDocument.set(documentUuid, rows);
        const key = rowKey(documentUuid, sourcePeriod);
        const months = rows.get(key) ?? new Set<string>();
        rows.set(key, months);
        months.add(monthKey);
    };

    Object.entries(verdictByUuid).forEach(([documentUuid, verdict]) => {
        // A coverage row holds its own month by definition, and keeps holding it while a staged
        // correction points it somewhere else.
        verdict.heldRows.forEach((period) => add(documentUuid, period, periodKey(period)));
    });
    // Where each row sits now. The map is already keyed by the month, so it is read back rather
    // than rebuilt from the owner.
    ownerByPeriodKey.forEach((owner, monthKey) => add(owner.documentUuid, owner.sourcePeriod, monthKey));

    const siblingMonths = new Map<string, Set<string>>();
    rowsByDocument.forEach((rows) => {
        rows.forEach((_, key) => {
            const siblings = new Set<string>();
            rows.forEach((months, otherKey) => {
                if (otherKey !== key) {
                    months.forEach((month) => siblings.add(month));
                }
            });
            // A row with no sibling is left out entirely, so `offerableMonths` can hand the menu
            // the candidate list it was given rather than a fresh copy of it on every poll tick.
            if (siblings.size > 0) {
                siblingMonths.set(key, siblings);
            }
        });
    });
    return siblingMonths;
}

/**
 * The months one row's menu may offer: the candidates, minus anything a sibling row holds.
 *
 * A month a sibling holds has no answer that does not invent an intent the member did not
 * express — displacing the sibling drops a month they never mentioned — so it is withheld rather
 * than guessed. The row's own month is never a sibling's, so it survives: the tick has something
 * to mark, and a row can always be moved back where it came from.
 */
export function offerableMonths(candidates: StatementPeriod[], siblingMonths?: Set<string>): StatementPeriod[] {
    if (!siblingMonths?.size) {
        return candidates;
    }
    return candidates.filter((candidate) => !siblingMonths.has(periodKey(candidate)));
}
